/**
 * Onboarding draft persistence.
 *
 * The Oura connect step performs a *full-page* OAuth redirect
 * (`window.location.href = authorize_url`). That navigation tears down the
 * React app, so every answer the user typed earlier in onboarding — life
 * stage, date of birth, sex, brain-dump — lives only in component
 * `useState` and is **lost** when the browser returns to `/onboarding?code=`.
 *
 * Before the demographics step (added 2026-06-18) that loss was cosmetic:
 * the submit still completed with a null life stage. Once date of birth
 * became required AND gated at submit (`!dobIso` → age-block → abort), the
 * wiped DOB started hard-blocking completion for anyone who connects Oura
 * mid-onboarding — they bounce back to the demographics → connect screens
 * in a loop and can never finish.
 *
 * Fix: snapshot the answers to `sessionStorage` immediately before the
 * redirect and rehydrate them on the return mount, so the age gate sees the
 * real DOB and the flow resumes intact. sessionStorage (not localStorage) so
 * the draft is scoped to the tab and naturally evaporates when the tab
 * closes; we also clear it explicitly once onboarding completes.
 */

const KEY = 'haiven_onboarding_draft_v1';

/**
 * Persist a snapshot of the in-progress onboarding answers. Best-effort:
 * a throwing/absent sessionStorage (Safari private mode, SSR) must never
 * break the connect flow, so failures are swallowed.
 *
 * @param {object} draft Plain serializable answers.
 */
export function saveOnboardingDraft(draft) {
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(draft || {}));
  } catch {
    // Known limitation: Safari private mode throws here, so the draft never
    // persists and the OAuth return rehydrates nothing — the original
    // wiped-DOB onboarding loop can still reproduce in that one environment.
  }
}

/**
 * Read the persisted onboarding answers, or an empty object when there is
 * no draft (or storage is unavailable / corrupt).
 *
 * @returns {object}
 */
export function loadOnboardingDraft() {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

/** Remove the persisted draft. Called once onboarding completes. */
export function clearOnboardingDraft() {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
}
